import supabase from "@/services/supabaseClient";

export const PHARMACY_MEMBER_ACCOUNT_BUCKET = "pharmacy-member-images";
export const PHARMACY_MEMBER_ACCOUNT_UPDATED_EVENT = "afrimed:pharmacy-member-account-updated";
export const PHARMACY_MEMBER_THEME_OPTIONS = ["system", "light", "dark"];

function safeText(value) {
  return String(value || "").trim();
}

function fileExtensionFor(file) {
  const namePart = safeText(file?.name).split(".").pop();
  const ext = safeText(namePart).toLowerCase();
  if (ext) return ext;
  if (safeText(file?.type) === "image/png") return "png";
  if (safeText(file?.type) === "image/webp") return "webp";
  return "jpg";
}

function normalizeThemePreference(value) {
  const next = safeText(value).toLowerCase();
  return PHARMACY_MEMBER_THEME_OPTIONS.includes(next) ? next : "";
}

export function broadcastPharmacyMemberAccountUpdated() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(PHARMACY_MEMBER_ACCOUNT_UPDATED_EVENT));
}

async function readPharmacyMemberAccountByUserId(userId = "") {
  const normalizedUserId = safeText(userId);
  if (!normalizedUserId) return null;

  const { data, error } = await supabase
    .from("pharmacy_member_accounts")
    .select("user_id,email,full_name,profile_photo_path,theme_preference,created_at,updated_at")
    .eq("user_id", normalizedUserId)
    .maybeSingle();

  if (error) throw error;
  return data || null;
}

async function ensurePharmacyMemberAccountRow({ userId, email, fullName } = {}) {
  const normalizedUserId = safeText(userId);
  if (!normalizedUserId) throw new Error("Pharmacy account is missing a user id.");

  const normalizedEmail = safeText(email).toLowerCase();
  const normalizedName = safeText(fullName);

  const existing = await readPharmacyMemberAccountByUserId(normalizedUserId);

  if (!existing) {
    const payload = {
      user_id: normalizedUserId,
      email: normalizedEmail,
      full_name: normalizedName || null,
      updated_at: new Date().toISOString(),
    };
    const { data, error } = await supabase
      .from("pharmacy_member_accounts")
      .insert(payload)
      .select("user_id,email,full_name,profile_photo_path,theme_preference,created_at,updated_at")
      .single();
    if (error) {
      const message = safeText(error?.message).toLowerCase();
      const duplicate =
        error?.code === "23505" ||
        message.includes("duplicate key") ||
        message.includes("unique constraint");
      if (!duplicate) throw error;
      const recovered = await readPharmacyMemberAccountByUserId(normalizedUserId);
      if (recovered) return recovered;
      throw error;
    }
    return data;
  }

  const patch = {};
  if (normalizedEmail && normalizedEmail !== safeText(existing.email).toLowerCase()) {
    patch.email = normalizedEmail;
  }
  if (normalizedName && !safeText(existing.full_name)) {
    patch.full_name = normalizedName;
  }

  if (!Object.keys(patch).length) return existing;

  patch.updated_at = new Date().toISOString();
  const { data, error } = await supabase
    .from("pharmacy_member_accounts")
    .update(patch)
    .eq("user_id", normalizedUserId)
    .select("user_id,email,full_name,profile_photo_path,theme_preference,created_at,updated_at")
    .single();

  if (error) throw error;
  return data;
}

export async function getPharmacyMemberAccount({ userId, email, fullName } = {}) {
  return ensurePharmacyMemberAccountRow({ userId, email, fullName });
}

export async function updatePharmacyMemberAccount({ userId, fullName } = {}) {
  const normalizedUserId = safeText(userId);
  if (!normalizedUserId) throw new Error("Pharmacy account is missing a user id.");

  await ensurePharmacyMemberAccountRow({ userId: normalizedUserId });

  const { data, error } = await supabase
    .from("pharmacy_member_accounts")
    .update({
      full_name: safeText(fullName) || null,
      updated_at: new Date().toISOString(),
    })
    .eq("user_id", normalizedUserId)
    .select("user_id,email,full_name,profile_photo_path,theme_preference,created_at,updated_at")
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error("We couldn’t save the pharmacy member account yet.");
  broadcastPharmacyMemberAccountUpdated();
  return data;
}

export async function updatePharmacyMemberThemePreference({ userId, themePreference } = {}) {
  const normalizedUserId = safeText(userId);
  if (!normalizedUserId) throw new Error("Pharmacy account is missing a user id.");

  const normalizedTheme = normalizeThemePreference(themePreference);
  if (!normalizedTheme) throw new Error("Pharmacy theme preference is invalid.");

  await ensurePharmacyMemberAccountRow({ userId: normalizedUserId });

  const { data, error } = await supabase
    .from("pharmacy_member_accounts")
    .update({
      theme_preference: normalizedTheme,
      updated_at: new Date().toISOString(),
    })
    .eq("user_id", normalizedUserId)
    .select("user_id,email,full_name,profile_photo_path,theme_preference,created_at,updated_at")
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error("We couldn’t save the pharmacy theme yet.");
  broadcastPharmacyMemberAccountUpdated();
  return data;
}

export async function getPharmacyMemberPhotoUrl(path, expiresInSeconds = 3600) {
  const cleanPath = safeText(path);
  if (!cleanPath) return "";

  const { data, error } = await supabase.storage
    .from(PHARMACY_MEMBER_ACCOUNT_BUCKET)
    .createSignedUrl(cleanPath, expiresInSeconds);

  if (error) {
    const message = safeText(error?.message).toLowerCase();
    if (
      message.includes("not found") ||
      message.includes("object not found") ||
      message.includes("no such object")
    ) {
      return "";
    }
    throw error;
  }

  return safeText(data?.signedUrl);
}

export async function uploadPharmacyMemberPhoto({ userId, file, previousPath = "" } = {}) {
  const normalizedUserId = safeText(userId);
  if (!normalizedUserId) throw new Error("Pharmacy account is missing a user id.");
  if (!(file instanceof File)) throw new Error("Please choose an image to upload.");

  const existingAccount = await ensurePharmacyMemberAccountRow({ userId: normalizedUserId });

  const ext = fileExtensionFor(file);
  const nextPath = `pharmacy-members/${normalizedUserId}/avatar-${Date.now()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from(PHARMACY_MEMBER_ACCOUNT_BUCKET)
    .upload(nextPath, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type || undefined,
    });

  if (uploadError) throw uploadError;

  const { data, error } = await supabase
    .from("pharmacy_member_accounts")
    .update({
      profile_photo_path: nextPath,
      updated_at: new Date().toISOString(),
    })
    .eq("user_id", normalizedUserId)
    .select("user_id,email,full_name,profile_photo_path,theme_preference,created_at,updated_at")
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error("We couldn’t save the pharmacy member image yet.");

  const previous = safeText(previousPath) || safeText(existingAccount?.profile_photo_path);
  if (previous && previous !== nextPath) {
    await supabase.storage.from(PHARMACY_MEMBER_ACCOUNT_BUCKET).remove([previous]);
  }

  broadcastPharmacyMemberAccountUpdated();
  return data;
}

export async function removePharmacyMemberPhoto({ userId, profilePhotoPath = "" } = {}) {
  const normalizedUserId = safeText(userId);
  if (!normalizedUserId) throw new Error("Pharmacy account is missing a user id.");

  const existingAccount = await ensurePharmacyMemberAccountRow({ userId: normalizedUserId });

  const existingPath = safeText(profilePhotoPath) || safeText(existingAccount?.profile_photo_path);
  const { data, error } = await supabase
    .from("pharmacy_member_accounts")
    .update({
      profile_photo_path: null,
      updated_at: new Date().toISOString(),
    })
    .eq("user_id", normalizedUserId)
    .select("user_id,email,full_name,profile_photo_path,theme_preference,created_at,updated_at")
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error("We couldn’t remove the pharmacy member image yet.");

  if (existingPath) {
    await supabase.storage.from(PHARMACY_MEMBER_ACCOUNT_BUCKET).remove([existingPath]);
  }

  broadcastPharmacyMemberAccountUpdated();
  return data;
}
